import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { OrganisationMinimalInterface } from 'src/app/model/organisations/organisation-minimal-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';

import {
  DataOfferPricingDto,
  DataOfferResponseDto,
  DataOfferUpsertRequestDto
} from '../../models/bautagebuch.models';
import { BautagebuchService } from '../../services/bautagebuch.service';

export interface ConnectorSectionOption {
  id: string;
  label: string;
  group: 'Kernbericht' | 'Leistungsdaten' | 'Dokumentation' | 'Intern';
  description: string;
}

export interface ConnectorAssetRules {
  reportStatuses: Array<'DRAFT' | 'FINALIZED'>;
  projectScope?: string;
  datePreset?: 'all' | 'current-month' | 'last-30-days' | 'custom';
  startDate?: string | null;
  endDate?: string | null;
  selectedSectionIds: string[];
  searchTerm: string;
}

export interface ConnectorAccessPolicy {
  visibility: 'public' | 'restricted';
  catalogAudienceLabel: string;
  consumerOrgIds: number[];
}

export interface ConnectorContractPolicy {
  mode: 'view-only' | 'compute-to-data' | 'controlled-export';
  permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>;
  exportFormats: string[];
  customTerms: { [key: string]: any };
}

export interface DataOfferVm {
  id: string | null;
  title: string;
  titleCustomized: boolean;
  description: string;
  status: 'PUBLISHED' | 'DRAFT' | 'REVOKED' | 'INVALID' | 'ARCHIVED';
  assetRules: ConnectorAssetRules;
  accessPolicy: ConnectorAccessPolicy;
  contractPolicy: ConnectorContractPolicy;
  pricing: DataOfferPricingDto;
  lastUpdated: string;
}

@Component({
  standalone: false,
  selector: 'app-connector-control-plane',
  templateUrl: './connector-control-plane.component.html',
  styleUrls: ['./connector-control-plane.component.scss']
})
export class ConnectorControlPlaneComponent implements OnInit, OnChanges, OnDestroy {
  @Input() organisationId: number | null = null;
  @Input() refreshNonce = 0;

  readonly sectionOptions: ConnectorSectionOption[] = [
    { id: 'project', label: 'Projekt / Baustelle', group: 'Kernbericht', description: 'Projektbezeichnung, GU, Gewerk und Baustellenkontext.' },
    { id: 'day', label: 'Tag / Arbeitszeit', group: 'Kernbericht', description: 'Datum, Vorarbeiter und Arbeitszeit des Berichts.' },
    { id: 'weather', label: 'Wetter', group: 'Kernbericht', description: 'Tageslage, Temperatur und Wetterbemerkungen.' },
    { id: 'nuFirma', label: 'NU Firma / Arbeitskräfte', group: 'Leistungsdaten', description: 'Firmenangaben, Arbeitskräfte und Zeiterfassung der Kolonne.' },
    { id: 'ausgefuehrteArbeiten', label: 'Ausgeführte Arbeiten', group: 'Leistungsdaten', description: 'Leistungsbeschreibung, Mengen und Tagesfortschritt.' },
    { id: 'behinderungen', label: 'Behinderungen', group: 'Dokumentation', description: 'Störungen, Ursachen und Auswirkungen auf die Leistung.' },
    { id: 'geraete', label: 'Geräte', group: 'Dokumentation', description: 'Eingesetzte Geräte und zugehörige Hinweise.' },
    { id: 'material', label: 'Material', group: 'Dokumentation', description: 'Materialverbrauch, Lieferungen und Engpässe.' },
    { id: 'weisungen', label: 'Weisungen', group: 'Dokumentation', description: 'Anweisungen der Bauleitung oder des GU.' },
    { id: 'pruefungen', label: 'Prüfungen', group: 'Dokumentation', description: 'Prüfungen, Freigaben und Abnahmen.' },
    { id: 'verdeckteLeistungen', label: 'Verdeckte Leistungen', group: 'Dokumentation', description: 'Leistungen, die vor dem Verdecken dokumentiert werden müssen.' },
    { id: 'vorkommnisse', label: 'Vorkommnisse', group: 'Dokumentation', description: 'Besondere Ereignisse, Vorfälle oder Abweichungen.' },
    { id: 'interneFelder', label: 'Interne Felder', group: 'Intern', description: 'Nur für interne Steuerung, standardmäßig nicht für Dritte bestimmt.' }
  ];

  offers: DataOfferVm[] = [];
  selectedOfferId: string | null = null;
  editingOffer: DataOfferVm | null = null;
  isCreating = false;
  availableOrganisations: OrganisationMinimalInterface[] = [];

  private readonly subscriptions = new Subscription();
  private loadSequence = 0;

  constructor(
    private readonly bautagebuchService: BautagebuchService,
    private readonly organisationService: OrganisationService,
    private readonly snackBar: MatSnackBar,
    private readonly cd: ChangeDetectorRef
  ) {}

  get stats() {
    return [
      { label: 'Veröffentlicht', value: this.offers.filter((offer) => offer.status === 'PUBLISHED').length },
      { label: 'Entwurf', value: this.offers.filter((offer) => offer.status === 'DRAFT').length },
      { label: 'Abschnitte', value: this.editingOffer?.assetRules.selectedSectionIds.length || 0 }
    ];
  }

  get dataSelectionLabel(): string {
    if (!this.editingOffer) {
      return '';
    }
    return this.reportStatusLabel(this.editingOffer.assetRules.reportStatuses);
  }

  get selectedOffer(): DataOfferVm | null {
    if (!this.selectedOfferId) {
      return null;
    }
    return this.offers.find((offer) => offer.id === this.selectedOfferId) || null;
  }

  get sectionSelection(): ConnectorSectionOption[] {
    const selectedIds = new Set(this.editingOffer?.assetRules.selectedSectionIds || []);
    return this.sectionOptions.filter((section) => selectedIds.has(section.id));
  }

  get groupedSelectedSections(): Array<{ title: string; items: ConnectorSectionOption[] }> {
    const groups = new Map<string, ConnectorSectionOption[]>();
    this.sectionSelection.forEach((section) => {
      const current = groups.get(section.group) || [];
      current.push(section);
      groups.set(section.group, current);
    });
    return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
  }

  get accessVisibilityLabel(): string {
    if (this.editingOffer?.accessPolicy.visibility === 'restricted') {
      const count = this.editingOffer.accessPolicy.consumerOrgIds?.length || 0;
      return count ? `Eingeschränkter Katalog (${count})` : 'Eingeschränkter Katalog';
    }
    return 'Federated Catalog';
  }

  get contractModeLabel(): string {
    switch (this.editingOffer?.contractPolicy.mode) {
      case 'compute-to-data':
        return 'Compute-to-Data';
      case 'controlled-export':
        return 'Kontrollierter Export';
      default:
        return 'View-Only';
    }
  }

  get exportFormatLabel(): string {
    const formats = this.editingOffer
      ? this.exportFormatsFromPermissions(this.editingOffer.contractPolicy.permissions)
      : [];
    return formats.length ? formats.join(', ') : 'Kein Export';
  }

  get primaryActionLabel(): string {
    return this.editingOffer?.status === 'PUBLISHED'
      ? 'Änderungen speichern'
      : 'Data Offer veröffentlichen';
  }

  get generatedTitlePreview(): string {
    return this.editingOffer ? this.generateOfferTitle(this.editingOffer) : '';
  }

  ngOnInit(): void {
    this.loadAvailableOrganisations();
    this.loadOffers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organisationId || changes.refreshNonce) {
      this.loadOffers();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadAvailableOrganisations(): void {
    this.subscriptions.add(
      this.organisationService.getMinimalOrganisationList().subscribe({
        next: (organisations) => {
          this.availableOrganisations = (organisations || []).sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'de')
          );
          this.cd.detectChanges();
        },
        error: () => {
          this.availableOrganisations = [];
          this.cd.detectChanges();
        }
      })
    );
  }

  private loadOffers(): void {
    if (!this.organisationId) {
      this.offers = [];
      this.selectedOfferId = null;
      this.editingOffer = null;
      this.isCreating = false;
      return;
    }

    const sequence = ++this.loadSequence;
    this.subscriptions.add(
      this.bautagebuchService.getMyDataOffers(this.organisationId).subscribe({
        next: (offers) => {
          if (sequence !== this.loadSequence) {
            return;
          }
          this.applyOffers((offers || []).map((offer) => this.toDataOfferVm(offer)), true);
        },
        error: () => {
          if (sequence !== this.loadSequence) {
            return;
          }
          this.applyOffers([], false);
          this.showError('Data Offers konnten nicht geladen werden.');
        }
      })
    );
  }

  selectOffer(offer: DataOfferVm): void {
    this.selectedOfferId = offer.id;
    this.editingOffer = this.cloneOffer(offer);
    this.isCreating = false;
  }

  createNewOffer(): void {
    const defaultOffer: DataOfferVm = {
      id: null,
      title: '',
      titleCustomized: false,
      description: 'Beschreiben Sie, welche NU-Daten dieses Angebot im Catalog sichtbar macht.',
      status: 'DRAFT',
      lastUpdated: new Date().toISOString(),
      assetRules: {
        reportStatuses: ['FINALIZED'],
        datePreset: 'all',
        startDate: null,
        endDate: null,
        selectedSectionIds: this.sectionOptions.filter((section) => section.group !== 'Intern').map((section) => section.id),
        searchTerm: ''
      },
      accessPolicy: {
        visibility: 'public',
        catalogAudienceLabel: 'Sichtbar für alle GU im Federated Catalog',
        consumerOrgIds: []
      },
      contractPolicy: {
        mode: 'view-only',
        permissions: ['VIEW'],
        exportFormats: [],
        customTerms: {}
      },
      pricing: {
        type: 'FREE',
        amount: null,
        currency: null,
        billingBasis: null
      }
    };

    this.selectedOfferId = null;
    this.editingOffer = this.withAutoTitle(defaultOffer);
    this.isCreating = true;
  }

  onTitleInput(title: string): void {
    if (!this.editingOffer) {
      return;
    }
    const autoTitle = this.generateOfferTitle(this.editingOffer);
    this.editingOffer = {
      ...this.editingOffer,
      title,
      titleCustomized: title.trim() !== autoTitle.trim()
    };
  }

  updateAssetRules(assetRules: ConnectorAssetRules): void {
    if (!this.editingOffer) {
      return;
    }
    this.editingOffer = this.withAutoTitle({
      ...this.editingOffer,
      assetRules
    });
  }

  updatePolicies(
    payload: {
      accessPolicy: ConnectorAccessPolicy;
      contractPolicy: ConnectorContractPolicy;
      pricing: DataOfferPricingDto;
    }
  ): void {
    if (!this.editingOffer) {
      return;
    }
    this.editingOffer = this.withAutoTitle({
      ...this.editingOffer,
      accessPolicy: payload.accessPolicy,
      contractPolicy: payload.contractPolicy,
      pricing: payload.pricing
    });
  }

  saveOffer(): void {
    if (!this.editingOffer || !this.organisationId) {
      return;
    }

    if (this.editingOffer.status === 'PUBLISHED') {
      this.subscriptions.add(
        this.persistOffer(this.editingOffer).subscribe({
          next: (offer) => {
            this.handleSaveSuccess(offer, 'Data Offer aktualisiert.');
          },
          error: () => this.showError('Data Offer konnte nicht gespeichert werden.')
        })
      );
      return;
    }

    this.subscriptions.add(
      this.persistOffer(this.editingOffer)
        .pipe(switchMap((offer) => this.bautagebuchService.publishDataOffer(this.organisationId!, offer.offerId)))
        .subscribe({
          next: (offer) => {
            this.handleSaveSuccess(offer, 'Data Offer veröffentlicht.');
          },
          error: () => this.showError('Data Offer konnte nicht veröffentlicht werden.')
        })
    );
  }

  saveDraft(): void {
    if (!this.editingOffer || !this.organisationId) {
      return;
    }

    const draft = {
      ...this.editingOffer,
      status: 'DRAFT' as const,
      lastUpdated: new Date().toISOString()
    };

    this.subscriptions.add(
      this.persistOffer(draft).subscribe({
        next: (offer) => {
          this.handleSaveSuccess(offer, 'Entwurf gespeichert.');
        },
        error: () => this.showError('Entwurf konnte nicht gespeichert werden.')
      })
    );
  }

  cancelEdit(): void {
    if (this.selectedOffer) {
      this.editingOffer = this.cloneOffer(this.selectedOffer);
      this.isCreating = false;
      return;
    }

    this.isCreating = false;
    this.editingOffer = null;
  }

  deleteOffer(offer: DataOfferVm, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!confirm('Möchten Sie dieses Data Offer wirklich löschen?')) {
      return;
    }

    if (!offer.id) {
      this.editingOffer = null;
      this.isCreating = false;
      return;
    }
    if (!this.organisationId) {
      return;
    }

    this.subscriptions.add(
      this.bautagebuchService.deleteDataOffer(this.organisationId, offer.id).subscribe({
        next: () => {
          this.removeOffer(offer.id!);
          this.showSuccess('Data Offer gelöscht.');
        },
        error: () => this.showError('Data Offer konnte nicht gelöscht werden.')
      })
    );
  }

  revokeOffer(offer: DataOfferVm, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.organisationId || !offer.id) {
      return;
    }
    if (!confirm('Möchten Sie dieses Data Offer wirklich widerrufen?')) {
      return;
    }

    this.subscriptions.add(
      this.bautagebuchService.revokeDataOffer(this.organisationId, offer.id).subscribe({
        next: (response) => {
          this.upsertOffer(this.toDataOfferVm(response));
          this.showSuccess('Data Offer widerrufen.');
        },
        error: () => this.showError('Data Offer konnte nicht widerrufen werden.')
      })
    );
  }

  invalidateOffer(offer: DataOfferVm, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.organisationId || !offer.id) {
      return;
    }
    if (!confirm('Moechten Sie dieses Data Offer wirklich als ungueltig markieren?')) {
      return;
    }

    this.subscriptions.add(
      this.bautagebuchService.invalidateDataOffer(this.organisationId, offer.id).subscribe({
        next: (response) => {
          this.upsertOffer(this.toDataOfferVm(response));
          this.showSuccess('Data Offer als ungueltig markiert.');
        },
        error: () => this.showError('Data Offer konnte nicht invalidiert werden.')
      })
    );
  }

  getOfferSubtitle(offer: DataOfferVm): string {
    const sectionCount = offer.assetRules.selectedSectionIds.length;
    const visibility =
      offer.accessPolicy.visibility === 'public' ? 'Federated Catalog' : 'Partnerkatalog';
    return `${this.getContractModeLabel(offer)} · ${sectionCount} Abschnitte · ${visibility}`;
  }

  getStatusLabel(status: DataOfferVm['status']): string {
    switch (status) {
      case 'PUBLISHED':
        return 'Veröffentlicht';
      case 'REVOKED':
        return 'Widerrufen';
      case 'ARCHIVED':
        return 'Archiviert';
      case 'INVALID':
        return 'Ungültig';
      default:
        return 'Entwurf';
    }
  }

  private persistOffer(offer: DataOfferVm): Observable<DataOfferResponseDto> {
    const request = this.toUpsertRequest(offer);
    return offer.id
      ? this.bautagebuchService.updateDataOffer(this.organisationId!, offer.id, request)
      : this.bautagebuchService.createDataOffer(this.organisationId!, request);
  }

  private handleSaveSuccess(response: DataOfferResponseDto, message: string): void {
    const offer = this.toDataOfferVm(response);
    this.upsertOffer(offer);
    this.selectedOfferId = offer.id;
    this.editingOffer = this.cloneOffer(offer);
    this.isCreating = false;
    this.showSuccess(message);
  }

  private upsertOffer(offer: DataOfferVm): void {
    const existingIndex = this.offers.findIndex((item) => item.id === offer.id);
    if (existingIndex >= 0) {
      this.offers = this.offers.map((item, index) => (index === existingIndex ? offer : item));
    } else {
      this.offers = [offer, ...this.offers];
    }
    this.offers = this.sortOffers(this.offers);
    this.cd.detectChanges();
  }

  private removeOffer(offerId: string): void {
    this.offers = this.offers.filter((offer) => offer.id !== offerId);
    if (this.selectedOfferId === offerId) {
      this.selectedOfferId = null;
      this.editingOffer = null;
      if (this.offers.length) {
        this.selectOffer(this.offers[0]);
      }
    }
  }

  private applyOffers(offers: DataOfferVm[], preserveSelection: boolean): void {
    this.offers = this.sortOffers(offers);
    if (preserveSelection && this.selectedOfferId) {
      const selected = this.offers.find((offer) => offer.id === this.selectedOfferId);
      if (selected) {
        this.editingOffer = this.cloneOffer(selected);
        return;
      }
    }
    if (this.offers.length) {
      this.selectOffer(this.offers[0]);
    } else {
      this.selectedOfferId = null;
      this.editingOffer = null;
      this.isCreating = false;
    }
    this.cd.detectChanges();
  }

  private sortOffers(offers: DataOfferVm[]): DataOfferVm[] {
    return [...offers].sort((left, right) => (right.lastUpdated || '').localeCompare(left.lastUpdated || ''));
  }

  private toDataOfferVm(offer: DataOfferResponseDto): DataOfferVm {
    const permissions = this.resolveUsagePermissions(offer.usagePolicy);
    const reportStatuses =
      (offer.reportFilter?.reportLifecycleStatuses || ['FINALIZED']).filter(
        (status): status is 'DRAFT' | 'FINALIZED' => status === 'DRAFT' || status === 'FINALIZED'
      );

    return {
      id: offer.offerId,
      title: offer.title,
      titleCustomized: !!offer.titleCustomized,
      description: offer.description || '',
      status: offer.status,
      lastUpdated: offer.updatedAt || offer.createdAt || new Date().toISOString(),
      assetRules: {
        reportStatuses: reportStatuses.length ? reportStatuses : ['FINALIZED'],
        datePreset: (offer.reportFilter?.startDate || offer.reportFilter?.endDate) ? 'custom' : 'all',
        startDate: offer.reportFilter?.startDate || null,
        endDate: offer.reportFilter?.endDate || null,
        projectScope: offer.reportFilter?.projectScope,
        selectedSectionIds: offer.includedSections || [],
        searchTerm: offer.reportFilter?.searchTerm || ''
      },
      accessPolicy: {
        visibility: offer.accessPolicy?.visibility || 'public',
        catalogAudienceLabel: this.buildCatalogAudienceLabel(
          offer.accessPolicy?.visibility || 'public',
          offer.accessPolicy?.consumerOrgIds || []
        ),
        consumerOrgIds: offer.accessPolicy?.consumerOrgIds || []
      },
      contractPolicy: {
        mode: this.modeFromPermissions(permissions),
        permissions,
        exportFormats: this.exportFormatsFromPermissions(permissions),
        customTerms: offer.usagePolicy?.customTerms || {}
      },
      pricing: offer.pricing || { type: 'FREE', amount: null, currency: null, billingBasis: null }
    };
  }

  private toUpsertRequest(offer: DataOfferVm): DataOfferUpsertRequestDto {
    return {
      title: offer.title,
      titleCustomized: offer.titleCustomized,
      description: offer.description,
      reportFilter: {
        reportLifecycleStatuses: offer.assetRules.reportStatuses,
        startDate: offer.assetRules.startDate || null,
        endDate: offer.assetRules.endDate || null,
        projectScope: offer.assetRules.projectScope,
        searchTerm: offer.assetRules.searchTerm
      },
      includedSections: offer.assetRules.selectedSectionIds,
      accessPolicy: {
        visibility: offer.accessPolicy.visibility,
        consumerOrgIds: offer.accessPolicy.consumerOrgIds
      },
      usagePolicy: {
        mode: this.modeFromPermissions(offer.contractPolicy.permissions),
        permissions: offer.contractPolicy.permissions,
        exportFormats: this.exportFormatsFromPermissions(offer.contractPolicy.permissions),
        customTerms: offer.contractPolicy.customTerms
      },
      pricing: offer.pricing
    };
  }

  private withAutoTitle(offer: DataOfferVm): DataOfferVm {
    if (offer.titleCustomized) {
      return offer;
    }
    return {
      ...offer,
      title: this.generateOfferTitle(offer)
    };
  }

  private generateOfferTitle(offer: DataOfferVm): string {
    const parts = [this.reportStatusLabel(offer.assetRules.reportStatuses)];
    const dateLabel = this.dateRangeLabel(offer.assetRules.startDate, offer.assetRules.endDate);
    if (dateLabel) {
      parts.push(dateLabel);
    }
    parts.push(this.usageLabel(offer.contractPolicy));
    parts.push(offer.pricing.type === 'PAID' ? 'kostenpflichtig' : 'kostenfrei');
    return parts.join(', ');
  }

  private reportStatusLabel(reportStatuses: ConnectorAssetRules['reportStatuses']): string {
    const hasDraft = reportStatuses.includes('DRAFT');
    const hasFinalized = reportStatuses.includes('FINALIZED');
    if (hasDraft && hasFinalized) {
      return 'Entwurfs- und finale Berichte';
    }
    if (hasDraft) {
      return 'Entwurfsberichte';
    }
    return 'Finalisierte Berichte';
  }

  private dateRangeLabel(startDate?: string | null, endDate?: string | null): string | null {
    if (!startDate && !endDate) {
      return null;
    }
    if (startDate && endDate) {
      return `${this.formatDateLabel(startDate)} bis ${this.formatDateLabel(endDate)}`;
    }
    if (startDate) {
      return `ab ${this.formatDateLabel(startDate)}`;
    }
    return `bis ${this.formatDateLabel(endDate!)}`;
  }

  private usageLabel(policy: ConnectorContractPolicy): string {
    if (this.isComputeOnly(policy.permissions)) {
      return 'Compute-to-Data';
    }
    const exportFormats = this.exportFormatsFromPermissions(policy.permissions);
    if (exportFormats.length) {
      return `${exportFormats.join('/')} Export`;
    }
    return 'Ansicht';
  }

  private resolveUsagePermissions(usagePolicy?: {
    permissions?: string[];
  } | null): Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'> {
    const explicitPermissions = (usagePolicy?.permissions || []).filter(
      (
        permission
      ): permission is 'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA' =>
        permission === 'VIEW' ||
        permission === 'EXPORT_PDF' ||
        permission === 'EXPORT_XML' ||
        permission === 'COMPUTE_TO_DATA'
    );
    if (explicitPermissions.length) {
      return this.normalizePermissions(explicitPermissions);
    }
    return ['VIEW'];
  }

  private normalizePermissions(
    permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>
  ): Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'> {
    const unique = Array.from(new Set(permissions));
    if (unique.includes('EXPORT_PDF') || unique.includes('EXPORT_XML')) {
      return unique.includes('VIEW') ? unique : [...unique, 'VIEW'];
    }
    return unique;
  }

  private exportFormatsFromPermissions(
    permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>
  ): string[] {
    const formats: string[] = [];
    if (permissions.includes('EXPORT_PDF')) {
      formats.push('PDF');
    }
    if (permissions.includes('EXPORT_XML')) {
      formats.push('XML');
    }
    return formats;
  }

  private isComputeOnly(
    permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>
  ): boolean {
    return permissions.includes('COMPUTE_TO_DATA') && !permissions.includes('VIEW');
  }

  private modeFromPermissions(
    permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>
  ): ConnectorContractPolicy['mode'] {
    if (this.isComputeOnly(permissions)) {
      return 'compute-to-data';
    }
    if (permissions.includes('EXPORT_PDF') || permissions.includes('EXPORT_XML')) {
      return 'controlled-export';
    }
    return 'view-only';
  }

  private buildCatalogAudienceLabel(
    visibility: ConnectorAccessPolicy['visibility'],
    consumerOrgIds: number[]
  ): string {
    if (visibility === 'restricted') {
      if (consumerOrgIds.length) {
        return `Nur für ${consumerOrgIds.length} Partner sichtbar`;
      }
      return 'Nur für ausgewählte Partner sichtbar';
    }
    return 'Sichtbar für alle GU im Federated Catalog';
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 4000 });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 6000, panelClass: ['snack-error'] });
  }

  private getContractModeLabel(offer: DataOfferVm): string {
    switch (offer.contractPolicy.mode) {
      case 'compute-to-data':
        return 'Compute-to-Data';
      case 'controlled-export':
        return 'Export kontrolliert';
      default:
        return 'View-Only';
    }
  }

  private cloneOffer(offer: DataOfferVm): DataOfferVm {
    return JSON.parse(JSON.stringify(offer)) as DataOfferVm;
  }

  private formatDateLabel(value: string): string {
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }
    return `${day}.${month}.${year}`;
  }

}
